/**
 * Layout Import Modal — 3-step wizard
 *
 * Step 1: Upload PDF
 * Step 2: Page Select (multi-page PDFs only; auto-skipped for single-page)
 * Step 3: Review classification results & confirm import
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useUIStore } from '../../stores/uiStore';
import { useProjectStore } from '../../stores/projectStore';
import { parseLayoutPdf } from '../../services/layoutParser';
import { getClassificationStats, CLASSIFIER_VERSION } from '../../domain/layout/classify';
import { runOcrOnCanvas, runOcrServer, ocrItemsToEntities, countClassifiedLabels } from '../../services/layoutOcr';
import { uploadAsset } from '../../services/assetApi';
import { pdfToTransparentPng, centerPlacementBounds } from '../../utils/planProxy';
import { dispatchPlanBake, syncCompanionPlacement } from '../../utils/planBakeDispatch';
import { attachDxfToOverlay } from '../../utils/dxfAttach';
import { PLAN_BAKE_DEFAULT_DPI } from '../../services/tileApi';
import type { LayoutTextEntity, SiteLayout } from '../../domain/layout/types';
import type { ClassifiedEntity } from '../../domain/layout/classify';
import './LayoutImportModal.css';

type WizardStep = 'upload' | 'page-select' | 'review';

interface PageThumb {
  pageNum: number;
  dataUrl: string;
  widthPt: number;
  heightPt: number;
}

export default function LayoutImportModal() {
  const closeModal = useUIStore((s) => s.closeModal);
  const addNotification = useUIStore((s) => s.addNotification);
  // F-01B: append-mode imports — no "replace existing?" prompt. The new
  // layout lands at the end of `siteLayouts[]` and becomes active; any
  // existing layouts are preserved.
  const addSiteLayoutAppend = useProjectStore((s) => s.addSiteLayoutAppend);
  const siteId = useProjectStore((s) => s.meta.siteId);

  const [step, setStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageThumbs, setPageThumbs] = useState<PageThumb[]>([]);
  const [selectedPage, setSelectedPage] = useState(0); // 0-indexed
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, message: '' });
  const [error, setError] = useState('');
  const [entities, setEntities] = useState<LayoutTextEntity[]>([]);
  // One-process import (owner workflow): labels are opt-out, a DXF can be
  // staged right here — one upload, one anchoring, everything follows.
  const [extractLabels, setExtractLabels] = useState(true);
  const [stagedDxf, setStagedDxf] = useState<File | null>(null);
  const dxfInputRef = useRef<HTMLInputElement>(null);
  const [pageWidthPt, setPageWidthPt] = useState(0);
  const [pageHeightPt, setPageHeightPt] = useState(0);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pageImageUrl, setPageImageUrl] = useState<string | null>(null);
  const [pageImageDim, setPageImageDim] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [whiteThreshold, setWhiteThreshold] = useState(245);
  const [dragOver, setDragOver] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrDone, setOcrDone] = useState(false);
  const [ocrProgress, setOcrProgress] = useState({ percent: 0, message: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (pageImageUrl) URL.revokeObjectURL(pageImageUrl);
      pageThumbs.forEach((t) => {
        if (t.dataUrl.startsWith('blob:')) URL.revokeObjectURL(t.dataUrl);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-render page image when white threshold changes
  const thresholdTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!arrayBuffer || !pageImageUrl) return;
    // Debounce to avoid re-rendering on every slider tick
    clearTimeout(thresholdTimerRef.current);
    thresholdTimerRef.current = setTimeout(() => {
      renderPageImage(arrayBuffer, selectedPage, whiteThreshold);
    }, 300);
    return () => clearTimeout(thresholdTimerRef.current);
  }, [whiteThreshold]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── File selection ──

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF file.');
      return;
    }
    if (selectedFile.size > 100 * 1024 * 1024) {
      setError('PDF file size exceeds 100MB limit.');
      return;
    }

    setError('');
    setFile(selectedFile);

    try {
      setLoading(true);
      const buf = await selectedFile.arrayBuffer();
      setArrayBuffer(buf);

      // Detect page count
      const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString();

      const pdf = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
      setTotalPages(pdf.numPages);

      if (pdf.numPages === 1) {
        // Single page — skip page select, go straight to parse
        await pdf.destroy();
        setSelectedPage(0);
        setStep('review');
        await runParse(buf, 0);
      } else {
        // Multi-page — generate thumbnails
        const thumbs: PageThumb[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelledRef.current) break;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d')!;
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;
          thumbs.push({
            pageNum: i,
            dataUrl: canvas.toDataURL('image/jpeg', 0.6),
            widthPt: page.getViewport({ scale: 1 }).width,
            heightPt: page.getViewport({ scale: 1 }).height,
          });
          page.cleanup();
        }
        await pdf.destroy();
        setPageThumbs(thumbs);
        setStep('page-select');
        setLoading(false);
      }
    } catch (err) {
      setError(`Failed to read PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parse PDF page ──

  const runParse = useCallback(async (buf: ArrayBuffer, pageIndex: number) => {
    setLoading(true);
    setProgress({ percent: 0, message: 'Starting...' });

    try {
      // Progress honesty: the worker's extraction is only PART of the work —
      // the preview render + transparency pass below can take seconds on a
      // big sheet, and the bar used to sit at "Done, 100%" through all of
      // it (owner complaint 2026-09-02). Worker progress maps to 0-80%;
      // the preview phases own 80-99; 100 appears when nothing remains.
      const result = await parseLayoutPdf(
        buf.slice(0), // copy — transfer moves ownership
        pageIndex,
        undefined, // no custom patterns yet
        { onProgress: (p, m) => setProgress({ percent: Math.round(p * 0.8), message: m }) },
      );

      setEntities(result.entities);
      setPageWidthPt(result.pageWidthPt);
      setPageHeightPt(result.pageHeightPt);
      setWarnings(result.warnings);

      // Render page image for preview + later use as map overlay
      await renderPageImage(buf, pageIndex, whiteThreshold, (p, m) =>
        setProgress({ percent: p, message: m }),
      );

      setProgress({ percent: 100, message: 'Done' });
      setLoading(false);
    } catch (err) {
      setError(`Parse failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setLoading(false);
    }
  }, []);

  const renderPageImage = useCallback(async (
    buf: ArrayBuffer,
    pageIndex: number,
    bgThreshold = 245,
    onProgress?: (percent: number, message: string) => void,
  ) => {
    onProgress?.(82, 'Rendering page preview...');
    const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();

    const pdf = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
    const page = await pdf.getPage(pageIndex + 1);
    const viewport = page.getViewport({ scale: 1 });

    // Cap render resolution to avoid GPU crashes.
    // Vector layouts are text-based — 3x scale gives sharp lines without
    // blowing out GPU memory (keeps canvas under ~16M pixels).
    const MAX_RENDER_DIM = 4096;
    const MAX_PIXEL_AREA = 16_000_000; // ~4000×4000
    let scale = Math.min(MAX_RENDER_DIM / viewport.width, MAX_RENDER_DIM / viewport.height, 3);
    const area = viewport.width * scale * viewport.height * scale;
    if (area > MAX_PIXEL_AREA) {
      scale = Math.sqrt(MAX_PIXEL_AREA / (viewport.width * viewport.height));
    }
    const renderViewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(renderViewport.width);
    canvas.height = Math.round(renderViewport.height);
    const ctx = canvas.getContext('2d')!;

    // Render on white first (PDF needs opaque background to render correctly)
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: renderViewport, canvas } as any).promise;

    onProgress?.(92, 'Removing white background...');
    // Make white/near-white pixels transparent so satellite imagery shows through
    if (bgThreshold > 0) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] >= bgThreshold && data[i + 1] >= bgThreshold && data[i + 2] >= bgThreshold) {
          data[i + 3] = 0;
        }
      }
      ctx.putImageData(imageData, 0, 0);
    }

    onProgress?.(97, 'Finalizing preview...');
    const dim = { w: canvas.width, h: canvas.height };
    const blob = await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b!), 'image/png'),
    );
    // Release canvas GPU memory immediately
    canvas.width = 0;
    canvas.height = 0;
    const url = URL.createObjectURL(blob);
    setPageImageUrl(url);
    setPageImageDim(dim);

    page.cleanup();
    await pdf.destroy();
  }, []);

  // ── OCR fallback ──

  const handleRunOcr = useCallback(async () => {
    if (!arrayBuffer || ocrRunning) return;

    setOcrRunning(true);
    setOcrProgress({ percent: 0, message: 'Preparing OCR...' });

    try {
      let ocrItems: Awaited<ReturnType<typeof runOcrOnCanvas>> | null = null;

      if (siteId) {
        // ── Server-side OCR (native Tesseract in the COG container) ──
        // Uploads the ~5MB PDF itself; the container renders the page in
        // tiles and reports per-tile progress. No canvas render at all on
        // this path — the browser build below is fallback-only.
        try {
          ocrItems = await runOcrServer(
            arrayBuffer.slice(0),
            selectedPage,
            pageWidthPt,
            pageHeightPt,
            siteId,
            (pct, msg) => setOcrProgress({ percent: pct, message: msg }),
          );
        } catch (serverErr) {
          console.warn('[OCR] server OCR failed, falling back to browser WASM:', serverErr);
          setOcrProgress({ percent: 2, message: 'Server OCR unavailable — running in the browser (slower)...' });
        }
      }

      if (!ocrItems) {
        // ── Browser WASM OCR (unsaved projects, or server path failed) ──
        // Render the page to an offscreen canvas. OCR uses 2D canvas (no
        // WebGL), so we can render larger than the 4096 GPU limit. 3x scale
        // (~216 DPI for A1) balances quality vs time. Cap at 8192px / 40M px.
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
          import.meta.url,
        ).toString();

        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;
        const page = await pdf.getPage(selectedPage + 1);
        const viewport = page.getViewport({ scale: 1 });

        const MAX_OCR_DIM = 8192;
        const MAX_OCR_AREA = 40_000_000;
        let ocrScale = Math.min(3, MAX_OCR_DIM / viewport.width, MAX_OCR_DIM / viewport.height);
        const ocrArea = viewport.width * ocrScale * viewport.height * ocrScale;
        if (ocrArea > MAX_OCR_AREA) {
          ocrScale = Math.sqrt(MAX_OCR_AREA / (viewport.width * viewport.height));
        }
        const ocrViewport = page.getViewport({ scale: ocrScale });

        const ocrCanvas = document.createElement('canvas');
        ocrCanvas.width = Math.round(ocrViewport.width);
        ocrCanvas.height = Math.round(ocrViewport.height);
        const ctx = ocrCanvas.getContext('2d')!;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, ocrCanvas.width, ocrCanvas.height);
        await page.render({ canvasContext: ctx, viewport: ocrViewport, canvas: ocrCanvas } as any).promise;

        page.cleanup();
        await pdf.destroy();

        try {
          ocrItems = await runOcrOnCanvas(
            ocrCanvas,
            viewport.width,
            viewport.height,
            (pct, msg) => setOcrProgress({ percent: pct, message: msg }),
          );
        } finally {
          // Release the up-to-40-megapixel canvas (~160MB) promptly.
          ocrCanvas.width = 0;
          ocrCanvas.height = 0;
        }
      }

      // Convert to entities and merge with existing
      const ocrEntities = ocrItemsToEntities(ocrItems, entities);
      if (ocrEntities.length > 0) {
        setEntities((prev) => [...prev, ...ocrEntities]);
      }

      setOcrDone(true);
      const classified = ocrEntities.filter(e => e.classification && e.classification.level !== 'infrastructure').length;
      setOcrProgress({ percent: 100, message: `OCR found ${ocrEntities.length} new labels (${classified} classified)` });
    } catch (err) {
      setOcrProgress({
        percent: 0,
        message: `OCR failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setOcrRunning(false);
    }
  }, [arrayBuffer, selectedPage, entities, ocrRunning, siteId, pageWidthPt, pageHeightPt]);

  // ── Handle import ──

  const handleImport = useCallback(() => {
    if (!file || !arrayBuffer) return;
    if (extractLabels && entities.length === 0) return;

    // WP-A one-upload convergence: the SAME PDF also becomes a plan overlay
    // (created async below), and the layout remembers it so the anchor
    // transform can position the sheet — one upload, one registration, plan
    // + labels + DXF all aligned. The id is minted up front so the layout
    // can be stamped synchronously.
    const companionOverlayId = siteId
      ? `overlay_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
      : undefined;

    // F-01B: append the new layout to siteLayouts[] without disturbing any
    // existing layouts. `addSiteLayoutAppend` also activates the new layout
    // so the editor panels immediately bind to it via the alias. No more
    // "Replace existing?" prompt — multi-layout is the new default.
    // Labels are opt-out (one-process wizard): unticked = plan-only import,
    // no layout is created and the sheet registers via the plan's own
    // Register flow instead of anchors.
    if (extractLabels) {
      const now = new Date().toISOString();
      const layout: SiteLayout = {
        id: `layout-${Date.now()}`,
        name: file.name.replace(/\.pdf$/i, ''),
        schemaVersion: 1,
        createdAt: now,
        pdfAssetUrl: undefined, // will be synced to R2 on save
        pageIndex: selectedPage,
        pageImage: pageImageUrl
          ? {
              url: pageImageUrl,
              widthPx: pageImageDim.w,
              heightPx: pageImageDim.h,
              pageWidthPt,
              pageHeightPt,
            }
          : undefined,
        textEntities: entities,
        classifierVersion: CLASSIFIER_VERSION,
        anchors: [],
        // F-01A: per-layout topologyExceptions is deprecated. Leave undefined
        // here; site-level `data.topologyExceptions` is authoritative.
        labelOpacity: 0.8,
        labelVisible: true,
        imageOpacity: 0.5,
        imageVisible: true,
        planOverlayId: companionOverlayId,
      };
      addSiteLayoutAppend(layout);
      addNotification({
        type: 'success',
        title: 'Layout imported',
        message: `${entities.length} text entities extracted from ${file.name}`,
      });
    }
    closeModal();

    // Companion plan creation — fire-and-forget so the modal stays snappy.
    // Failure leaves a dangling planOverlayId, which every consumer treats
    // as a no-op; the user can still add the plan manually as before.
    if (companionOverlayId && siteId) {
      void (async () => {
        try {
          addNotification({
            type: 'info',
            title: 'Preparing the plan',
            message: 'Rendering the sheet from the same PDF — it appears in Plans Layout in a few seconds.',
          });
          const rendered = await pdfToTransparentPng(file, selectedPage + 1);
          let imageUrl = rendered.url;
          let sourceR2Key: string | undefined;
          try {
            const uploaded = await uploadAsset(file, siteId, 'overlay', file.name);
            sourceR2Key = uploaded.r2Key;
          } catch (err) {
            console.warn('[LayoutImport] companion PDF upload failed; bake unavailable:', err);
          }
          try {
            const proxyBlob = await fetch(imageUrl).then((r) => r.blob());
            const proxy = await uploadAsset(
              proxyBlob, siteId, 'overlay',
              `${file.name.replace(/\.pdf$/i, '')}_preview.png`);
            URL.revokeObjectURL(imageUrl);
            imageUrl = proxy.url;
          } catch (err) {
            console.warn('[LayoutImport] companion preview upload failed:', err);
          }
          useProjectStore.getState().addOverlay({
            id: companionOverlayId,
            name: file.name.replace(/\.pdf$/i, '.png'),
            url: imageUrl,
            bounds: centerPlacementBounds(),
            opacity: 0.75,
            visible: true,
            sourceR2Key,
            bake: {
              pageIndex: selectedPage,
              pageWidthPt: rendered.pageWidthPt,
              pageHeightPt: rendered.pageHeightPt,
              dpi: PLAN_BAKE_DEFAULT_DPI,
              whiteThreshold: 245,
            },
          });
          // `addOverlay` issues the D1 create fire-and-forget and, when it
          // fails, quietly removes the overlay again — leaving the layout
          // pointing at a plan that never existed and nothing downstream
          // (placement, bake, DXF re-align) able to fire, forever, with no
          // symptom but an absence. Observed on Tel Yitzhak 2026-08-30. Wait
          // for the create and treat a rollback as a hard failure so the
          // catch below actually tells the user.
          try {
            await useProjectStore.getState().flushPendingEntityWrites();
          } catch (err) {
            console.warn('[LayoutImport] companion create flush failed:', err);
          }
          const landed = useProjectStore.getState().data.overlays
            .some((o) => o.id === companionOverlayId);
          if (!landed) throw new Error('the plan sheet could not be saved to this site');

          // One-process wizard: a DXF staged at import attaches now that the
          // overlay exists. The ingest fits with whatever layout state exists
          // at ready-time; the anchor session's Done re-aligns afterwards.
          if (stagedDxf) {
            try {
              addNotification({
                type: 'info',
                title: 'Uploading DXF',
                message: `${stagedDxf.name} — layers appear on the plan when the server finishes reading it.`,
              });
              await attachDxfToOverlay(companionOverlayId, stagedDxf);
            } catch (err) {
              addNotification({
                type: 'warning',
                title: 'DXF attach failed',
                message: `${err instanceof Error ? err.message : 'Unknown error'} — attach it from the plan's row later.`,
              });
            }
          }

          // The creation/anchoring RACE, both directions: if the user already
          // finished anchoring while this rendered+uploaded (proven possible
          // on the first WP-A smoke), position the sheet from the existing
          // transform NOW and bake — nothing later would do it otherwise.
          // Plan-only imports (labels unticked) skip this: the active layout
          // is some OTHER sheet's — syncing would move the wrong plan.
          const placed = extractLabels && await syncCompanionPlacement();
          if (placed) {
            try {
              const outcome = await dispatchPlanBake(companionOverlayId);
              if (outcome === 'started') {
                addNotification({
                  type: 'info',
                  title: 'Baking the plan at full resolution',
                  message: 'Placement came from your anchors — takes a few minutes.',
                });
              }
            } catch (err) {
              console.warn('[LayoutImport] companion auto-bake failed:', err);
            }
          } else {
            addNotification({
              type: 'info',
              title: 'Plan added from the same PDF',
              message: 'Place the layout anchors and the plan sheet will position itself.',
            });
          }
        } catch (err) {
          console.warn('[LayoutImport] companion plan creation failed:', err);
          // Silence here is what made this expensive: the user was told the
          // plan was on its way, it never arrived, and the only remaining
          // signal was a console line. Say it failed, and say what fixes it —
          // importing the same PDF from Plans Layout now re-links itself.
          addNotification({
            type: 'warning',
            title: 'Plan sheet not created',
            message: `${err instanceof Error ? err.message : 'Unknown error'} — import the same PDF from the Plans Layout ticket; it will re-link to this layout and place itself.`,
          });
        }
      })();
    }
  }, [file, arrayBuffer, entities, selectedPage, pageImageUrl, pageImageDim, pageWidthPt, pageHeightPt, siteId, extractLabels, stagedDxf, addSiteLayoutAppend, addNotification, closeModal]);

  // ── Page select confirm ──

  const handlePageConfirm = useCallback(async () => {
    if (!arrayBuffer) return;
    setStep('review');
    await runParse(arrayBuffer, selectedPage);
  }, [arrayBuffer, selectedPage, runParse]);

  // ── Drop zone handlers ──

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  // ── Classification stats ──

  const stats = entities.length > 0 ? getClassificationStats(entities as ClassifiedEntity[]) : null;

  // ── Render ──

  return (
    <div className="layout-import-backdrop" onClick={(e) => e.target === e.currentTarget && !loading && closeModal()}>
      <div className="layout-import-modal">
        {/* Header */}
        <div className="layout-import-header">
          <h2>Import Vector Layout</h2>
          <button className="layout-import-close" onClick={closeModal} disabled={loading} type="button">
            &times;
          </button>
        </div>

        {/* Steps indicator */}
        <div className="layout-import-steps">
          <span className={`layout-import-step${step === 'upload' ? ' layout-import-step--active' : ''}`}>
            1. Upload
          </span>
          <span className={`layout-import-step${step === 'page-select' ? ' layout-import-step--active' : ''}`}>
            2. Page
          </span>
          <span className={`layout-import-step${step === 'review' ? ' layout-import-step--active' : ''}`}>
            3. Review
          </span>
        </div>

        {/* Body */}
        <div className="layout-import-body">
          {/* ── Step 1: Upload ── */}
          {step === 'upload' && (
            <div className="layout-import-upload">
              <div
                className={`layout-import-dropzone${dragOver ? ' layout-import-dropzone--dragover' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <polyline points="9 15 12 12 15 15"/>
                </svg>
                <p>Drop PDF file here or click to browse</p>
                <span className="layout-import-dropzone__hint">
                  Vector PDF with text labels (site string layout)
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                }}
              />
              {loading && (
                <div className="layout-import-progress">
                  <div className="layout-import-progress__bar">
                    <div
                      className="layout-import-progress__fill"
                      style={{ width: `${progress.percent}%` }}
                    />
                  </div>
                  <span className="layout-import-progress__text">{progress.message}</span>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Page Select ── */}
          {step === 'page-select' && (
            <div className="layout-import-pages">
              <p className="layout-import-pages__hint">
                {totalPages} pages found. Select the page containing the string layout.
              </p>
              <div className="layout-import-page-grid">
                {pageThumbs.map((t) => (
                  <button
                    key={t.pageNum}
                    className={`layout-import-page-thumb${selectedPage === t.pageNum - 1 ? ' layout-import-page-thumb--selected' : ''}`}
                    onClick={() => setSelectedPage(t.pageNum - 1)}
                    type="button"
                  >
                    <img src={t.dataUrl} alt={`Page ${t.pageNum}`} />
                    <span>Page {t.pageNum}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step === 'review' && (
            <div className="layout-import-review">
              {loading ? (
                <div className="layout-import-progress">
                  <div className="layout-import-progress__bar">
                    <div
                      className="layout-import-progress__fill"
                      style={{ width: `${progress.percent}%` }}
                    />
                  </div>
                  <span className="layout-import-progress__text">{progress.message}</span>
                </div>
              ) : (
                <>
                  {/* Preview image */}
                  {pageImageUrl && (
                    <div className="layout-import-preview">
                      <img src={pageImageUrl} alt="PDF page preview" />
                    </div>
                  )}

                  {/* White removal threshold */}
                  <div className="ep-threshold-row">
                    <span className="ep-threshold-label">White removal</span>
                    <input type="range" className="ep-threshold-slider" min="0" max="255" step="1" value={whiteThreshold} onChange={(e) => setWhiteThreshold(Number(e.target.value))} />
                    <span className="ep-threshold-val">{whiteThreshold === 0 ? 'Off' : whiteThreshold}</span>
                  </div>

                  {/* One-process import options: the plan sheet is always
                      created; labels are opt-out; a DXF can be staged now.
                      One upload + one anchoring session completes all three. */}
                  <div className="layout-import-options">
                    <label className="layout-import-options__row">
                      <input
                        type="checkbox"
                        checked={extractLabels}
                        onChange={(e) => setExtractLabels(e.target.checked)}
                      />
                      <span>
                        Extract labels (vector layout)
                        {!extractLabels && ' — plan-only: register the sheet with its own Register button'}
                      </span>
                    </label>
                    <div className="layout-import-options__row">
                      <input
                        ref={dxfInputRef}
                        type="file"
                        accept=".dxf"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (f) setStagedDxf(f);
                        }}
                      />
                      <button
                        type="button"
                        className="layout-import-btn layout-import-btn--secondary layout-import-btn--sm"
                        onClick={() => dxfInputRef.current?.click()}
                      >
                        {stagedDxf ? 'Change DXF' : 'Attach DXF (optional)'}
                      </button>
                      {stagedDxf && (
                        <span className="layout-import-options__dxf">
                          {stagedDxf.name}
                          <button type="button" title="Remove" onClick={() => setStagedDxf(null)}>×</button>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  {stats && (
                    <div className="layout-import-stats">
                      <div className="layout-import-stats__header">
                        Classification Results — {entities.length} entities
                      </div>
                      <div className="layout-import-stats__chips">
                        {stats.string > 0 && (
                          <span className="layout-chip layout-chip--string">String: {stats.string}</span>
                        )}
                        {stats.inverter > 0 && (
                          <span className="layout-chip layout-chip--inverter">Inverter: {stats.inverter}</span>
                        )}
                        {stats.dccb > 0 && (
                          <span className="layout-chip layout-chip--dccb">DCCB: {stats.dccb}</span>
                        )}
                        {stats.station > 0 && (
                          <span className="layout-chip layout-chip--station">Station: {stats.station}</span>
                        )}
                        {stats.module > 0 && (
                          <span className="layout-chip layout-chip--module">Module: {stats.module}</span>
                        )}
                        {stats.row > 0 && (
                          <span className="layout-chip layout-chip--row">Row: {stats.row}</span>
                        )}
                        {stats.zone > 0 && (
                          <span className="layout-chip layout-chip--zone">Zone: {stats.zone}</span>
                        )}
                        {stats.infrastructure > 0 && (
                          <span className="layout-chip layout-chip--infra">Infra: {stats.infrastructure}</span>
                        )}
                        {stats.unclassified > 0 && (
                          <span className="layout-chip layout-chip--unknown">Unclassified: {stats.unclassified}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* OCR fallback banner — show when few classified labels detected */}
                  {stats && !ocrDone && !ocrRunning && countClassifiedLabels(entities) < 20 && (
                    <div className="layout-import-ocr-banner">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                      <span>
                        Few labels detected — text may be rendered as vector graphics (CAD export).
                        Try OCR to extract labels from the page image.
                      </span>
                      <button
                        className="layout-import-btn layout-import-btn--primary layout-import-btn--sm"
                        onClick={handleRunOcr}
                        type="button"
                      >
                        Run OCR
                      </button>
                    </div>
                  )}

                  {/* OCR progress */}
                  {ocrRunning && (
                    <div className="layout-import-progress" style={{ marginBottom: 'var(--sl-space-4)' }}>
                      <div className="layout-import-progress__bar">
                        <div
                          className="layout-import-progress__fill"
                          style={{ width: `${ocrProgress.percent}%` }}
                        />
                      </div>
                      <span className="layout-import-progress__text">{ocrProgress.message}</span>
                    </div>
                  )}

                  {/* OCR done banner */}
                  {ocrDone && !ocrRunning && (
                    <div className="layout-import-ocr-done">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <span>{ocrProgress.message}</span>
                    </div>
                  )}

                  {/* Warnings */}
                  {warnings.length > 0 && (
                    <div className="layout-import-warnings">
                      {warnings.map((w, i) => (
                        <div key={i} className="layout-import-warning">{w}</div>
                      ))}
                    </div>
                  )}

                  {/* Sample labels — classified */}
                  {entities.length > 0 && (
                    <div className="layout-import-samples">
                      <div className="layout-import-samples__header">Sample Labels</div>
                      <div className="layout-import-samples__list">
                        {entities
                          .filter((e) => e.classification && e.classification.level !== 'infrastructure')
                          .slice(0, 12)
                          .map((e) => (
                            <span key={e.id} className={`layout-sample layout-sample--${e.classification?.level || 'unknown'}`}>
                              {e.normalizedText}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Sample unclassified — helps debug missing patterns */}
                  {stats && stats.unclassified > 0 && (
                    <div className="layout-import-samples">
                      <div className="layout-import-samples__header">
                        Unclassified Samples ({stats.unclassified} total)
                      </div>
                      <div className="layout-import-samples__list">
                        {entities
                          .filter((e) => !e.classification)
                          .slice(0, 20)
                          .map((e) => (
                            <span key={e.id} className="layout-sample layout-sample--unknown">
                              {e.normalizedText}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Error */}
          {error && <div className="layout-import-error">{error}</div>}
        </div>

        {/* Footer */}
        <div className="layout-import-footer">
          {step === 'page-select' && (
            <>
              <button
                className="layout-import-btn layout-import-btn--secondary"
                onClick={() => { setStep('upload'); setFile(null); setArrayBuffer(null); setPageThumbs([]); }}
                type="button"
              >
                Back
              </button>
              <button
                className="layout-import-btn layout-import-btn--primary"
                onClick={handlePageConfirm}
                type="button"
              >
                Use Page {selectedPage + 1}
              </button>
            </>
          )}
          {step === 'review' && !loading && (
            <>
              <button
                className="layout-import-btn layout-import-btn--secondary"
                onClick={() => { setStep(totalPages > 1 ? 'page-select' : 'upload'); setEntities([]); setOcrDone(false); }}
                disabled={ocrRunning}
                type="button"
              >
                Back
              </button>
              <button
                className="layout-import-btn layout-import-btn--primary"
                onClick={handleImport}
                disabled={(extractLabels && entities.length === 0) || ocrRunning}
                type="button"
              >
                {ocrRunning ? 'OCR running...'
                  : extractLabels ? `Import Layout (${entities.length} entities)` : 'Import Plan only'}
              </button>
            </>
          )}
          {step === 'upload' && !loading && (
            <button
              className="layout-import-btn layout-import-btn--secondary"
              onClick={closeModal}
              type="button"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
